import { useState } from "react";

import { Button, Card, PageSection, StatusMessage } from "@ceedero/ui";

import { useChangeUsername, type AccountProfile } from "@/account/hooks/use-account";
import { resolveCredentialChangeErrorKey } from "@/account/utils/account-refusal";
import { validateCurrentPassword, validateUsername } from "@/account/utils/credential-validation";
import { FormField } from "@/components/form-field";
import { useTranslation } from "@/localization/hooks/use-translation";

import type { TranslationKey } from "@/localization/hooks/use-translation";

interface FieldErrors {
  currentPassword?: TranslationKey | undefined;
  username?: TranslationKey | undefined;
}

/**
 * Changing the username, which is the name the user signs in with.
 *
 * The current password is asked for even though the browser is already signed
 * in: a session is evidence that somebody signed in once, possibly hours ago on
 * an unattended machine, not that the person typing now knows the password.
 * Re-asking is what stops a borrowed browser from becoming a taken account.
 *
 * Nothing is signed out by this — a session names a user, not a name.
 */
export const UsernameSection = ({ profile }: { profile: AccountProfile }) => {
  const { t } = useTranslation();
  const changeUsername = useChangeUsername();
  const [username, setUsername] = useState(profile.username ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isUnchanged = username.trim() === (profile.username ?? "");
  const refusalKey = resolveCredentialChangeErrorKey(changeUsername.error);

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const trimmed = username.trim();
    const errors: FieldErrors = {
      currentPassword: validateCurrentPassword(currentPassword),
      username: validateUsername(trimmed),
    };
    setFieldErrors(errors);
    if (errors.username !== undefined || errors.currentPassword !== undefined) {
      return;
    }
    // The password is held only as long as this call: it is in no store, no
    // cache and no query key, and it is cleared as soon as the API has it.
    changeUsername.mutate(
      { currentPassword, username: trimmed },
      { onSuccess: () => setCurrentPassword("") },
    );
  };

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((errors) =>
      errors[field] === undefined ? errors : { ...errors, [field]: undefined },
    );
    changeUsername.reset();
  };

  return (
    <PageSection
      title={t("account.username.title")}
      description={t("account.username.description")}
    >
      <Card className="p-5">
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          <FormField
            label={t("account.username.label")}
            isLabelHidden
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
              clearFieldError("username");
            }}
            hint={t("account.username.hint")}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={64}
            errorMessage={fieldErrors.username === undefined ? undefined : t(fieldErrors.username)}
          />

          <FormField
            label={t("account.username.currentPassword")}
            type="password"
            value={currentPassword}
            onChange={(event) => {
              setCurrentPassword(event.target.value);
              clearFieldError("currentPassword");
            }}
            autoComplete="current-password"
            errorMessage={
              fieldErrors.currentPassword === undefined ? undefined : t(fieldErrors.currentPassword)
            }
          />

          {refusalKey !== undefined && <StatusMessage tone="error">{t(refusalKey)}</StatusMessage>}
          {changeUsername.isSuccess && (
            <StatusMessage tone="success">{t("account.username.saved")}</StatusMessage>
          )}

          <div className="flex justify-end">
            <Button type="submit" isLoading={changeUsername.isPending} disabled={isUnchanged}>
              {t("account.save")}
            </Button>
          </div>
        </form>
      </Card>
    </PageSection>
  );
};
